import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  RadioGroup,
  Radio,
  Card,
  Grid,
  Paper,
  Chip,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PhoneIcon from "@mui/icons-material/Phone";
import SmsIcon from "@mui/icons-material/Sms";
import CallIcon from "@mui/icons-material/Call";

import PersonIcon from "@mui/icons-material/Person";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { countryCodes } from "./data/countryCode";
import AddPhoneNumber from "../services/AddPhoneNumber";
import RequestCodeApi from "../services/RequestCodeApi";
import SubmitCodeVerify from "../services/SubmitCodeVerify";

// Country data with dial codes

const PhoneVerificationDialog = ({ open, onClose, handleSync, headers }) => {
  const countries = countryCodes;
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Step 1 form data
  const [step1Data, setStep1Data] = useState({
    numberType: "new",
    cc: "91",
    phoneNumber: "",
    profileName: "",
  });

  // Step 2 form data
  const [step2Data, setStep2Data] = useState({
    phoneNumberId: "",
    mode: "sms",
  });

  // Step 3 form data
  const [step3Data, setStep3Data] = useState({
    phoneNumberId: "",
    code: "",
  });

  const steps = ["Phone Details", "Verification Method", "Verify Code"];

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setStep1Data({
        numberType: "new",
        cc: "91",
        phoneNumber: "",
        profileName: "",
      });
      setStep2Data({ phoneNumberId: "", mode: "sms" });
      setStep3Data({ phoneNumberId: "", code: "" });
      setError("");
      setSuccess("");
    }
  }, [open]);

  // API call simulation
  const callApi = async (endpoint, data) => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Simulate successful response
      const result = {
        phoneNumberId: `PHN_${Date.now()}`,
        success: true,
      };

      setSuccess("Operation completed successfully!");
      return result;
    } catch (err) {
      setError(err.message || "Something went wrong");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleStep1Submit = async () => {
    try {
      const result = await AddPhoneNumber(headers, step1Data);
      setStep2Data((prev) => ({
        ...prev,
        phoneNumberId: result.id,
      }));
      setStep3Data((prev) => ({
        ...prev,
        phoneNumberId: result.id,
      }));
      setActiveStep(1);
    } catch (err) {
      // Error is already set in callApi
    }
  };

  const handleStep2Submit = async () => {
    try {
      await RequestCodeApi(headers, step2Data);
      setActiveStep(2);
    } catch (err) {
      // Error is already set in callApi
    }
  };

  const handleStep3Submit = async () => {
    try {
      await SubmitCodeVerify(headers, step3Data);
      setSuccess("Phone number verified successfully!");

      // Wait a bit for the backend to process
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Call handleSync and wait for it to complete
      await handleSync();

      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      // Error is already set in callApi
    }
  };

  const handleNext = () => {
    if (activeStep === 0) handleStep1Submit();
    else if (activeStep === 1) handleStep2Submit();
    else if (activeStep === 2) handleStep3Submit();
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError("");
    setSuccess("");
  };

  const handleStep1Change = (field, value) => {
    setStep1Data((prev) => ({ ...prev, [field]: value }));
  };

  const handleStep2Change = (field, value) => {
    setStep2Data((prev) => ({ ...prev, [field]: value }));
  };

  const handleStep3Change = (field, value) => {
    setStep3Data((prev) => ({ ...prev, [field]: value }));
  };

  const isStep1Valid = () => {
    return (
      step1Data.phoneNumber.length >= 10 &&
      (step1Data.numberType === "migration" ||
        step1Data.profileName.trim() !== "")
    );
  };

  const isStep2Valid = () => {
    return step2Data.phoneNumberId.trim() !== "";
  };

  const isStep3Valid = () => {
    return step3Data.code.length === 6 && /^\d+$/.test(step3Data.code);
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            {/* <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography
                variant="h5"
                gutterBottom
                fontWeight="700"
                color="text.primary"
              >
                Register Your Phone Number
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Secure your account with phone verification
              </Typography>
            </Box> */}

            <Paper
              elevation={0}
              sx={{ p: 3, mb: 2, backgroundColor: "#f8f9fa", borderRadius: 2 }}
            >
              <Typography
                variant="subtitle2"
                gutterBottom
                fontWeight="600"
                color="text.primary"
                sx={{ mb: 2 }}
              >
                Select Registration Type
              </Typography>
              <RadioGroup
                value={step1Data.numberType}
                onChange={(e) =>
                  handleStep1Change("numberType", e.target.value)
                }
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Card
                    sx={{
                      flex: 1,
                      p: 2.5,
                      cursor: "pointer",
                      border: 2,
                      borderColor:
                        step1Data.numberType === "new"
                          ? "primary.main"
                          : "transparent",
                      backgroundColor:
                        step1Data.numberType === "new"
                          ? "rgba(102, 126, 234, 0.04)"
                          : "white",
                      transition: "all 0.2s",
                      "&:hover": {
                        borderColor: "primary.main",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      },
                    }}
                    onClick={() => handleStep1Change("numberType", "new")}
                  >
                    <FormControlLabel
                      value="new"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight="600">
                            ✨ New Number
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Register a fresh phone number
                          </Typography>
                        </Box>
                      }
                      sx={{ m: 0, width: "100%" }}
                    />
                  </Card>
                  <Card
                    sx={{
                      flex: 1,
                      p: 2.5,
                      cursor: "pointer",
                      border: 2,
                      borderColor:
                        step1Data.numberType === "migration"
                          ? "primary.main"
                          : "transparent",
                      backgroundColor:
                        step1Data.numberType === "migration"
                          ? "rgba(102, 126, 234, 0.04)"
                          : "white",
                      transition: "all 0.2s",
                      "&:hover": {
                        borderColor: "primary.main",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      },
                    }}
                    onClick={() => handleStep1Change("numberType", "migration")}
                  >
                    <FormControlLabel
                      value="migration"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight="600">
                            🔄 Migration
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Transfer existing number
                          </Typography>
                        </Box>
                      }
                      sx={{ m: 0, width: "100%" }}
                    />
                  </Card>
                </Box>
              </RadioGroup>
            </Paper>

            <Paper
              elevation={0}
              sx={{ p: 3, backgroundColor: "#f8f9fa", borderRadius: 2 }}
            >
              <Typography
                variant="subtitle2"
                gutterBottom
                fontWeight="600"
                color="text.primary"
                sx={{ mb: 2.5 }}
              >
                Enter Phone Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="medium">
                    <InputLabel>Country</InputLabel>
                    <Select
                      value={step1Data.cc}
                      label="Country"
                      onChange={(e) => handleStep1Change("cc", e.target.value)}
                    >
                      {countries.map((country) => (
                        <MenuItem key={country.code} value={country.code}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography>{country.flag}</Typography>
                            <Typography fontWeight="500">
                              +{country.code} - {country.label}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    label="Phone Number"
                    value={step1Data.phoneNumber}
                    onChange={(e) =>
                      handleStep1Change(
                        "phoneNumber",
                        e.target.value.replace(/\D/g, "")
                      )
                    }
                    placeholder="Enter 10-digit number"
                    fullWidth
                    inputProps={{ maxLength: 10 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              {step1Data.numberType === "new" && (
                <TextField
                  label="Profile Name"
                  value={step1Data.profileName}
                  onChange={(e) =>
                    handleStep1Change("profileName", e.target.value)
                  }
                  placeholder="Enter your full name"
                  fullWidth
                  sx={{ mt: 2.5 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            </Paper>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            {/* <Box sx={{ textAlign: "center", mb: 4 }}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto",
                  mb: 2,
                  boxShadow: "0 8px 16px rgba(245, 87, 108, 0.3)",
                }}
              >
                <SecurityIcon sx={{ fontSize: 36, color: "white" }} />
              </Box>
              <Typography
                variant="h5"
                gutterBottom
                fontWeight="700"
                color="text.primary"
              >
                Choose Verification Method
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select how you'd like to receive your code
              </Typography>
            </Box> */}

            <Paper
              elevation={0}
              sx={{ p: 3, mb: 2, backgroundColor: "#f8f9fa", borderRadius: 2 }}
            >
              <TextField
                label="Phone Number ID"
                value={step2Data.phoneNumberId}
                onChange={(e) =>
                  handleStep2Change("phoneNumberId", e.target.value)
                }
                placeholder="Auto-generated ID"
                fullWidth
                disabled
                sx={{ mb: 3 }}
                helperText="This ID is automatically assigned to your number"
              />

              <Typography
                variant="subtitle2"
                gutterBottom
                fontWeight="600"
                color="text.primary"
                sx={{ mb: 2 }}
              >
                Verification Method
              </Typography>
              <RadioGroup
                value={step2Data.mode}
                onChange={(e) => handleStep2Change("mode", e.target.value)}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Card
                    sx={{
                      flex: 1,
                      p: 3,
                      cursor: "pointer",
                      border: 2,
                      borderColor:
                        step2Data.mode === "sms"
                          ? "primary.main"
                          : "transparent",
                      backgroundColor:
                        step2Data.mode === "sms"
                          ? "rgba(102, 126, 234, 0.04)"
                          : "white",
                      transition: "all 0.2s",
                      "&:hover": {
                        borderColor: "primary.main",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      },
                    }}
                    onClick={() => handleStep2Change("mode", "sms")}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        mb: 1.5,
                      }}
                    >
                      <SmsIcon
                        sx={{ mr: 1.5, color: "primary.main", fontSize: 28 }}
                      />
                      <FormControlLabel
                        value="sms"
                        control={<Radio />}
                        label=""
                        sx={{ m: 0, ml: "auto" }}
                      />
                    </Box>
                    <Typography variant="body1" fontWeight="600" gutterBottom>
                      SMS Message
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Get a 6-digit code via text message
                    </Typography>
                    <Chip
                      label="Recommended"
                      size="small"
                      color="primary"
                      sx={{ mt: 1.5 }}
                    />
                  </Card>

                  <Card
                    sx={{
                      flex: 1,
                      p: 3,
                      cursor: "pointer",
                      border: 2,
                      borderColor:
                        step2Data.mode === "voice"
                          ? "primary.main"
                          : "transparent",
                      backgroundColor:
                        step2Data.mode === "voice"
                          ? "rgba(102, 126, 234, 0.04)"
                          : "white",
                      transition: "all 0.2s",
                      "&:hover": {
                        borderColor: "primary.main",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      },
                    }}
                    onClick={() => handleStep2Change("mode", "voice")}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        mb: 1.5,
                      }}
                    >
                      <CallIcon
                        sx={{ mr: 1.5, color: "primary.main", fontSize: 28 }}
                      />
                      <FormControlLabel
                        value="voice"
                        control={<Radio />}
                        label=""
                        sx={{ m: 0, ml: "auto" }}
                      />
                    </Box>
                    <Typography variant="body1" fontWeight="600" gutterBottom>
                      Voice Call
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Receive code via automated call
                    </Typography>
                  </Card>
                </Box>
              </RadioGroup>
            </Paper>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto",
                  mb: 2,
                  boxShadow: "0 8px 16px rgba(79, 172, 254, 0.3)",
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 36, color: "white" }} />
              </Box>
              <Typography
                variant="h5"
                gutterBottom
                fontWeight="700"
                color="text.primary"
              >
                Enter Verification Code
              </Typography>
              <Typography variant="body2" color="text.secondary">
                We've sent a code to +{step1Data.cc} {step1Data.phoneNumber}
              </Typography>
            </Box>

            <Paper
              elevation={0}
              sx={{ p: 3, backgroundColor: "#f8f9fa", borderRadius: 2 }}
            >
              <TextField
                label="Phone Number ID"
                value={step3Data.phoneNumberId}
                onChange={(e) =>
                  handleStep3Change("phoneNumberId", e.target.value)
                }
                placeholder="Your phone number ID"
                fullWidth
                disabled
                sx={{ mb: 3 }}
              />

              <TextField
                label="Verification Code"
                value={step3Data.code}
                onChange={(e) =>
                  handleStep3Change(
                    "code",
                    e.target.value.replace(/\D/g, "").slice(0, 6)
                  )
                }
                placeholder="000000"
                fullWidth
                inputProps={{
                  maxLength: 6,
                  style: {
                    textAlign: "center",
                    fontSize: "24px",
                    letterSpacing: "8px",
                    fontWeight: 600,
                  },
                }}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "white",
                  },
                }}
                helperText={`Enter the 6-digit code sent via ${
                  step2Data.mode === "sms" ? "SMS" : "voice call"
                }`}
              />

              <Box
                sx={{
                  textAlign: "center",
                  mt: 3,
                  pt: 3,
                  borderTop: "1px dashed #ddd",
                }}
              >
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Didn't receive the code?
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setActiveStep(1)}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Resend Code
                </Button>
              </Box>
            </Paper>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
        },
      }}
    >
      <DialogContent sx={{ position: "relative", p: 4 }}>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            color: "grey.500",
            backgroundColor: "grey.100",
            width: 36,
            height: 36,
            "&:hover": {
              backgroundColor: "grey.200",
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Stepper
          activeStep={activeStep}
          sx={{
            mb: 4,
            "& .MuiStepLabel-label": {
              fontWeight: 600,
              fontSize: "0.875rem",
            },
            "& .MuiStepIcon-root": {
              fontSize: "1.75rem",
            },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3, borderRadius: 2 }}
            onClose={() => setError("")}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 3, borderRadius: 2 }}
            icon={<CheckCircleIcon />}
          >
            {success}
          </Alert>
        )}

        {getStepContent(activeStep)}

        <Box
          sx={{
            display: "flex",
            gap: 2,
            mt: 4,
            pt: 3,
            borderTop: "2px solid #f0f0f0",
          }}
        >
          <Button
            onClick={handleBack}
            disabled={activeStep === 0 || loading}
            variant="outlined"
            size="large"
            startIcon={<ArrowBackIcon />}
            sx={{
              flex: 1,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              borderWidth: 2,
              "&:hover": {
                borderWidth: 2,
              },
            }}
          >
            Back
          </Button>

          <Button
            variant="contained"
            onClick={handleNext}
            disabled={
              (activeStep === 0 && !isStep1Valid()) ||
              (activeStep === 1 && !isStep2Valid()) ||
              (activeStep === 2 && !isStep3Valid()) ||
              loading
            }
            endIcon={loading ? null : <ArrowForwardIcon />}
            size="large"
            sx={{
              flex: 2,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
              backgroundColor: "primary.main",
              "&:hover": {
                backgroundColor: "primary.dark",
              },
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
                Processing...
              </>
            ) : activeStep === steps.length - 1 ? (
              "Verify Now"
            ) : (
              "Continue"
            )}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PhoneVerificationDialog;

// Demo wrapper
